import { useEffect, useState } from "react";
import { api, money, catColor } from "@/hub/lib/api";
import { Plus, Pencil, Trash2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

function useRole() {
  const [role, setRole] = useState(() => localStorage.getItem("lush_role") || "RM");
  useEffect(() => {
    const h = () => setRole(localStorage.getItem("lush_role") || "RM");
    window.addEventListener("role-change", h);
    return () => window.removeEventListener("role-change", h);
  }, []);
  return role;
}

export default function Activities() {
  const [activities, setActivities] = useState([]);
  const [categories, setCategories] = useState([]);
  const role = useRole();
  const isAdmin = role === "Admin";
  const load = () => api.activities().then(setActivities);
  useEffect(() => { load(); api.categories().then(setCategories); }, []);

  const grouped = categories.map((c) => ({ cat: c.name, items: activities.filter((a) => a.category === c.name) }));
  const uncategorized = activities.filter((a) => !categories.some((c) => c.name === a.category));
  if (uncategorized.length) grouped.push({ cat: "Other", items: uncategorized });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-stone-500 max-w-lg">Facilities & activities grouped by category. Prices drive per-group add-on / top-up costs. {!isAdmin && <span className="inline-flex items-center gap-1 text-terracotta"><Lock className="h-3 w-3" /> Switch to Admin to edit.</span>}</p>
        {isAdmin && <ActivityDialog categories={categories} onDone={load} />}
      </div>

      <div className="space-y-5">
        {grouped.map(({ cat, items }) => (
          <div key={cat} className="bg-white border border-border">
            <div className="px-5 py-3 border-b border-border flex items-center gap-2">
              <span className="h-3 w-3 rounded-sm" style={{ background: catColor(cat) }} />
              <h3 className="font-heading text-base font-medium">{cat}</h3>
              <span className="text-xs text-stone-400">{items.length} items</span>
            </div>
            {items.length === 0 ? (
              <div className="px-5 py-4 text-xs text-stone-400">No items yet in this category.</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {items.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0 hover:bg-stone-50 transition-colors" data-testid={`activity-row-${a.id}`}>
                      <td className="px-5 py-2.5 font-medium">{a.name}</td>
                      <td className="px-5 py-2.5 text-stone-400 text-xs">{a.unit}</td>
                      <td className="px-5 py-2.5 text-right font-mono">{money(a.cost)}</td>
                      {isAdmin && (
                        <td className="px-5 py-2.5 text-right w-24">
                          <div className="flex justify-end gap-1">
                            <ActivityDialog existing={a} categories={categories} onDone={load} trigger={
                              <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" data-testid={`edit-activity-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            } />
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm text-red-500 hover:bg-red-50" data-testid={`delete-activity-${a.id}`}
                              onClick={() => { if (window.confirm("Delete activity?")) api.deleteActivity(a.id).then(() => { toast.success("Deleted"); load(); }); }}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityDialog({ existing, categories, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", cost: "", unit: "per person", description: "" });

  useEffect(() => {
    if (open) {
      const firstCat = categories[0]?.name || "";
      setForm(existing
        ? { name: existing.name, category: existing.category, cost: existing.cost, unit: existing.unit, description: existing.description || "" }
        : { name: "", category: firstCat, cost: "", unit: "per person", description: "" });
    }
  }, [open, existing, categories]);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    if (!form.category) return toast.error("Pick a category");
    const payload = { ...form, cost: Number(form.cost) || 0 };
    try {
      if (existing) await api.updateActivity(existing.id, payload);
      else await api.createActivity(payload);
      toast.success(existing ? "Activity updated" : "Activity added");
      setOpen(false); onDone();
    } catch { toast.error("Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button size="sm" className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="new-activity-btn"><Plus className="h-4 w-4 mr-1" /> New Activity</Button>}
      </DialogTrigger>
      <DialogContent className="rounded-sm">
        <DialogHeader><DialogTitle className="font-heading">{existing ? "Edit Activity" : "New Activity"}</DialogTitle><DialogDescription>Set the item name, category and price.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="af-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-sm" data-testid="af-category"><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-stone-400">Unit</Label>
              <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                <SelectTrigger className="rounded-sm" data-testid="af-unit"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="per person">per person</SelectItem><SelectItem value="per event">per event</SelectItem><SelectItem value="per hour">per hour</SelectItem><SelectItem value="per night">per night</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs uppercase tracking-wider text-stone-400">Cost (LKR)</Label><Input type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="rounded-sm font-mono" data-testid="af-cost" /></div>
        </div>
        <DialogFooter><Button onClick={submit} className="rounded-sm bg-forest hover:bg-forest-hover" data-testid="save-activity-btn">{existing ? "Save" : "Add Activity"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
